import {
  House,
  MessageCircle,
  Store,
  ShoppingCart,
  Package,
  Megaphone,
  User,
  BarChart3,
  History,
  Shield,
  Wallet,
  DollarSign,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  MapPin,
  Bell,
  FileClock,
  Heart,
  ScrollText,
  Globe,
  Sprout,
  Star,
  UserCheck,
  Users,
  Search,
  Activity,
  Construction,
  LockKeyhole,
  Database,
  FileKey,
  Tractor,
  TrendingUp,
  PackageCheck,
  PackageX,
  Scale,
  BedDouble,
  Tag,
  Gavel,
  Flag,
  Truck,
  type LucideIcon,
} from 'lucide-react'
import {
  DEFAULT_VERTICAL,
  getRoleLabel,
  getVerticalMeta,
  isDrivingVertical,
  type Vertical,
} from './useVertical'

interface NavItem {
  label: string
  path: string
  icon: LucideIcon
  /**
   * Heading this item sits under in the sidebar. Only Ops uses these: nineteen
   * flat entries mixing money, trust, logistics, and platform read as one
   * undifferentiated list. Roles with a short nav stay ungrouped — a heading per
   * two items is noise.
   */
  section?: string
  /**
   * Show this item in the mobile tab bar, which renders five entries and no more.
   *
   * Sidebar order and tab-bar order are different problems. The sidebar wants every
   * item grouped under its section; the phone wants one representative entry per thing
   * you do. Taking the first five of a grouped list gave a seller-driver
   * Overview · Selling · three Driving items and pushed Earnings — the reason most
   * people open the app — off the bar entirely.
   */
  primary?: boolean
}

// Consumer nav is composed as: head + active-vertical items + shared tail.
// Only the per-vertical block changes when the active vertical switches; the
// head (Overview) and tail (Favorites/Notifications/History/Profile) are shared.
// Favorites is now available in every vertical (Farm-to-Table produce, homestay
// properties, thrift listings); the /favorites page is vertical-aware.
const CONSUMER_HEAD: NavItem[] = [{ label: 'Overview', path: '/consumer', icon: House }]

const CONSUMER_BY_VERTICAL: Record<Vertical, NavItem[]> = {
  farm_to_table: [
    { label: 'Cart', path: '/cart', icon: ShoppingCart },
    { label: 'My Orders', path: '/orders', icon: Package },
    { label: 'My Reservations', path: '/reservations', icon: Tractor },
    { label: 'Announcements', path: '/announcements', icon: Megaphone },
  ],
  stays: [
    { label: 'Browse', path: '/homestays', icon: Search },
    { label: 'My Stays', path: '/homestay-bookings', icon: BedDouble },
  ],
  thrifts: [
    { label: 'Browse', path: '/thrifts', icon: Tag },
    { label: 'My Offers', path: '/thrifts/offers', icon: Gavel },
    { label: 'My Purchases', path: '/thrifts/purchases', icon: Package },
  ],
  moves: [
    { label: 'Book a move', path: '/moves/book', icon: Truck },
    { label: 'My Moves', path: '/moves/mine', icon: Package },
  ],
}

const CONSUMER_SHARED: NavItem[] = [
  { label: 'Messages', path: '/messages', icon: MessageCircle },
  { label: 'Favorites', path: '/favorites', icon: Heart },
  { label: 'Notifications', path: '/notifications', icon: Bell },
  { label: 'Activity', path: '/history', icon: FileClock },
  { label: 'Profile', path: '/profile', icon: User },
]

// Overview is the workspace home for every role, so it carries the same House icon
// in all of them and in all three verticals — CONSUMER_HEAD, here, and FULFILLER_NAV.
// It used to be BarChart3, which collided with Insights in CREATOR_SHARED: on mobile
// the tab bar is the first five entries, so a thrift seller saw the same chart glyph
// for Workspace and Insights with no label to tell them apart.
const CREATOR_HEAD: NavItem[] = [{ label: 'Overview', path: '/creator', icon: House }]

const CREATOR_BY_VERTICAL: Record<Vertical, NavItem[]> = {
  farm_to_table: [
    { label: 'My Listings', path: '/my-listings', icon: Package },
    { label: 'Experiences', path: '/creator/experiences', icon: Tractor },
    { label: 'My Announcements', path: '/my-announcements', icon: Megaphone },
    { label: 'Growing Now', path: '/creator/growing', icon: Sprout },
    { label: 'Pickups', path: '/pickups', icon: MapPin },
    // Not BarChart3 — that is Insights in the shared tail below, and the two sat in
    // the same nav under one glyph. Demand is inbound signal from buyers; Insights
    // reports on listings you already have.
    { label: 'Demand Dashboard', path: '/demand', icon: TrendingUp },
    { label: 'Blogs', path: '/creator/blogs', icon: ScrollText },
    // The creator public page is a garden/farm profile — Farm-to-Table-specific copy and
    // fields, so it does not belong in the shared tail.
    { label: 'Public Page', path: '/creator/public-page', icon: Globe },
  ],
  stays: [{ label: 'Homestays', path: '/host/homestays', icon: BedDouble }],
  thrifts: [{ label: 'My Items', path: '/sell/listings', icon: Tag }],
  // Moves supply is the fulfiller/carrier side (Work context), not a creator listing.
  moves: [],
}

const CREATOR_SHARED: NavItem[] = [
  // Cross-vertical: the analytics engine resolves each vertical's own metrics, so this
  // belongs in the shared tail rather than being repeated per vertical (design/44).
  { label: 'Insights', path: '/insights', icon: BarChart3 },
  { label: 'Messages', path: '/messages', icon: MessageCircle },
  { label: 'Reviews', path: '/my-reviews', icon: Star },
  { label: 'Notifications', path: '/notifications', icon: Bell },
  { label: 'Activity', path: '/history', icon: FileClock },
  { label: 'Profile', path: '/profile', icon: User },
]

const FULFILLER_NAV: NavItem[] = [
  { label: 'Overview', path: '/fulfiller', icon: House },
  { label: 'Available Jobs', path: '/fulfiller/jobs', icon: PackageCheck },
  { label: 'Thrift Deliveries', path: '/fulfiller/thrift-deliveries', icon: Tag },
  { label: 'Trips', path: '/fulfiller/trips', icon: MapPin },
  { label: 'Vehicle', path: '/fulfiller/vehicle', icon: UserCheck },
  // `/fulfiller/history` is claimed jobs; `/history` is the cross-role activity log.
  // Both read "History" under the same glyph, so this nav offered two
  // identical-looking entries to two different pages — the same collision the
  // Overview comment above records, one nav down. Renaming the shared log to
  // "Activity" fixes it wherever it appears, since it is one route and the page
  // itself is titled "Activity History".
  { label: 'Job History', path: '/fulfiller/history', icon: History },
  { label: 'Earnings', path: '/earnings', icon: DollarSign },
  // Absent here while both other marketplace navs carried it, which left the one
  // role that is out on the road with no way to reach the other party.
  { label: 'Messages', path: '/messages', icon: MessageCircle },
  { label: 'Notifications', path: '/notifications', icon: Bell },
  { label: 'Activity', path: '/history', icon: FileClock },
  { label: 'Profile', path: '/profile', icon: User },
]

// Grouped by what the operator is doing, not by which vertical the queue came
// from. The vertical-specific queues (experience deposits, homestay payments,
// thrift moderation, hub console) sit beside their cross-vertical siblings under
// the same heading, because the operator's task is the same either way.
const OPS_NAV: NavItem[] = [
  // Platform overview first — operators land here and scan queues before diving in.
  { label: 'Health', path: '/ops/health', icon: Activity, section: 'Platform' },
  { label: 'Activity', path: '/history', icon: FileClock, section: 'Platform' },

  // One entry, not three: receipt verification is a single task, and splitting it
  // by vertical is what let thrifts ship with no queue at all.
  { label: 'Payment Verification', path: '/ops/verification', icon: CheckCircle, section: 'Money' },
  { label: 'Float Dashboard', path: '/ops/float', icon: Wallet, section: 'Money' },
  { label: 'Disbursements', path: '/ops/disbursements', icon: DollarSign, section: 'Money' },
  { label: 'Transactions', path: '/ops/transactions', icon: Search, section: 'Money' },

  {
    label: 'ID Verification',
    path: '/ops/id-verification',
    icon: UserCheck,
    section: 'Trust & safety',
  },
  // Homestay *property* approval is a different job from verifying a payment, so
  // it keeps its own entry — and belongs with the other vetting work, not Money.
  {
    label: 'Homestay Approvals',
    path: '/ops/homestays',
    icon: BedDouble,
    section: 'Trust & safety',
  },
  { label: 'Disputes', path: '/ops/disputes', icon: AlertTriangle, section: 'Trust & safety' },
  {
    label: 'Thrift Disputes',
    path: '/ops/thrift-disputes',
    icon: Scale,
    section: 'Trust & safety',
  },
  { label: 'Thrift Moderation', path: '/ops/thrifts', icon: Flag, section: 'Trust & safety' },
  { label: 'Participants', path: '/ops/participants', icon: Users, section: 'Trust & safety' },

  { label: 'Active Trips', path: '/ops/trips', icon: MapPin, section: 'Fulfillment' },
  { label: 'Hub Console', path: '/ops/hub-console', icon: PackageCheck, section: 'Fulfillment' },
  { label: 'Incidents', path: '/ops/incidents', icon: Construction, section: 'Fulfillment' },
  {
    // Not AlertTriangle — Disputes carries that in the section above, and the two
    // are different work: a dispute is adjudicated between parties, an exception is
    // a delivery that went wrong.
    label: 'Exceptions',
    path: '/ops/exceptions',
    icon: PackageX,
    section: 'Fulfillment',
  },
  { label: 'Escalations', path: '/ops/escalations', icon: AlertCircle, section: 'Fulfillment' },
  { label: 'Manual Confirm', path: '/ops/confirm', icon: Shield, section: 'Fulfillment' },
]

const ADMIN_NAV: NavItem[] = [
  { label: 'Admin Home', path: '/admin', icon: LockKeyhole },
  { label: 'Master Data', path: '/admin/master-data', icon: Database },
  { label: 'Access Control', path: '/admin/access', icon: FileKey },
  { label: 'Audit Log', path: '/admin/audit', icon: Shield },
  { label: 'Profile', path: '/profile', icon: User },
]

const ONBOARDING_NAV: NavItem[] = [
  { label: 'Complete Setup', path: '/onboarding', icon: CheckCircle },
  { label: 'Marketplace', path: '/marketplace', icon: ShoppingCart },
  { label: 'Explore Farms', path: '/explore-farms', icon: Sprout },
  { label: 'Upcoming Events', path: '/upcoming-events', icon: Megaphone },
  { label: 'Profile', path: '/profile', icon: User },
]

/**
 * The supply side is named differently in each vertical — Creator, Host, Seller — and
 * `roleLabels` is already the single place that mapping lives (`getRoleLabel`). A fixed
 * "Become a seller" would have called a homestay host a seller in their own nav.
 */
function becomeSellerItem(vertical: Vertical): NavItem {
  // Always offered when not yet enrolled — page shows the form or "available soon".
  return {
    label: `Become a ${getRoleLabel(vertical, 'creator', 'Seller')}`,
    path: '/become-seller',
    icon: Store,
  }
}

/**
 * Enrolment statuses that unlock selling surfaces (list/draft) in this vertical.
 * Matches backend DRAFTABLE_STATUSES — pending may draft while ops reviews.
 */
function canAccessSellingSurfaces(
  enrolments: Record<string, string>,
  vertical: string,
): boolean {
  const status = enrolments[vertical]
  return status === 'active' || status === 'pending'
}

/**
 * Whether Buying/Work should offer "Become a …" for this market vertical.
 *
 * Hidden once the account can already sell or draft here (`active` / `pending`).
 * Still offered when never applied, or when `rejected` / `suspended` (resubmit / appeal).
 * Moves uses Become a Fulfiller instead.
 */
export function shouldOfferBecomeSeller(
  vertical: Vertical,
  enrolments: Record<string, string>,
): boolean {
  if (vertical === 'moves') return false
  return !canAccessSellingSurfaces(enrolments, vertical)
}

/**
 * Buying and Work — two contexts, not three role modes (Spec 45 §9).
 *
 *   Buying — vertical-scoped. Changes when you switch vertical.
 *   Work   — selling (per vertical) + driving (**Moves only**).
 *
 * After the Moves vertical, fulfiller work is no longer cross-vertical chrome inside
 * every market. Fulfiller UI is only offered when the active vertical is Moves.
 * Farm-to-Table, Stays and Thrifts expose consumer + creator (seller/host) only.
 */
export type NavContext = 'buying' | 'work'

const WORK_OVERVIEW: NavItem = { label: 'Overview', path: '/creator', icon: House, primary: true }
/** Moves has no creator listings — overview is fulfiller home or Moves customer home. */
const MOVES_CUSTOMER_OVERVIEW: NavItem = {
  label: 'Overview',
  path: '/consumer',
  icon: House,
  primary: true,
}
const DRIVING_OVERVIEW: NavItem = {
  label: 'Overview',
  path: '/fulfiller',
  icon: House,
  primary: true,
}

/** Fulfiller work — only composed into Work when vertical is Moves. */
const DRIVING_ITEMS: NavItem[] = [
  {
    label: 'Available Jobs',
    path: '/fulfiller/jobs',
    icon: PackageCheck,
    section: 'Fulfiller',
    primary: true,
  },
  {
    label: 'Thrift Deliveries',
    path: '/fulfiller/thrift-deliveries',
    icon: Tag,
    section: 'Fulfiller',
  },
  { label: 'Trips', path: '/fulfiller/trips', icon: MapPin, section: 'Fulfiller' },
  { label: 'Job History', path: '/fulfiller/history', icon: History, section: 'Fulfiller' },
  { label: 'Vehicle', path: '/fulfiller/vehicle', icon: UserCheck, section: 'Fulfiller' },
]

function becomeFulfillerItem(): NavItem {
  return {
    label: 'Become a Fulfiller',
    path: '/become-fulfiller',
    icon: PackageCheck,
    section: 'Fulfiller',
    primary: true,
  }
}

/** Person-level tail: one balance, one inbox, one account — never per vertical. */
const WORK_TAIL: NavItem[] = [
  { label: 'Earnings', path: '/earnings', icon: DollarSign, primary: true },
  { label: 'Insights', path: '/insights', icon: BarChart3 },
  { label: 'Messages', path: '/messages', icon: MessageCircle },
  { label: 'Reviews', path: '/my-reviews', icon: Star },
  { label: 'Notifications', path: '/notifications', icon: Bell },
  { label: 'Activity', path: '/history', icon: FileClock },
  { label: 'Profile', path: '/profile', icon: User, primary: true },
]

/**
 * Whether "Work" is offered at all.
 *
 * Only once there is work to do: a buyer-only account gets no empty Selling and Driving
 * tabs, which is the cost the full-merge layout would have carried. The invitation to
 * start selling lives in the Buying nav instead, where the person who has not started
 * yet is actually looking.
 */
export function hasWorkContext(
  enrolments: Record<string, string> = {},
  heldRoles: string[] = [],
): boolean {
  return Object.keys(enrolments).length > 0 || heldRoles.includes('fulfiller')
}

/**
 * Which context a marketplace role lands in. `null` for ops/admin/onboarding, which
 * are not marketplace participation at all and keep their own navs.
 *
 * Derived rather than stored: `activeRole` is already persisted across reloads, and
 * introducing a second persisted value would give a returning user two sources of truth
 * that can disagree.
 */
export function navContextFor(role: string | null): NavContext | null {
  if (role === 'consumer') return 'buying'
  if (role === 'creator' || role === 'fulfiller') return 'work'
  return null
}

export function getNavForContext(
  context: NavContext,
  vertical: Vertical = DEFAULT_VERTICAL,
  enrolments: Record<string, string> = {},
  heldRoles: string[] = [],
): NavItem[] {
  if (context === 'buying') return getNavForRole('consumer', vertical, enrolments, heldRoles)

  const sellsHere = canAccessSellingSurfaces(enrolments, vertical)
  // Driving only under Moves — not on FTT / Stays / Thrifts Work chrome.
  const drivesHere = heldRoles.includes('fulfiller') && isDrivingVertical(vertical)
  const canOfferDriving = isDrivingVertical(vertical)
  const sellingLabel = `Selling · ${getVerticalMeta(vertical).label}`

  // Moves has no creator listings; skip an empty "Selling · Moves" block.
  // Once enrolled (active or pending draft), show selling surfaces — never "Become a …".
  const sellingItems = CREATOR_BY_VERTICAL[vertical] ?? CREATOR_BY_VERTICAL[DEFAULT_VERTICAL]
  const selling: NavItem[] =
    vertical === 'moves'
      ? []
      : sellsHere
        ? sellingItems.map((item, index) => ({
            ...item,
            section: sellingLabel,
            primary: index === 0,
          }))
        : shouldOfferBecomeSeller(vertical, enrolments)
          ? [{ ...becomeSellerItem(vertical), section: sellingLabel, primary: true }]
          : []

  const driving: NavItem[] = !canOfferDriving
    ? []
    : drivesHere
      ? DRIVING_ITEMS
      : [becomeFulfillerItem()]

  // Moves: never land Work Overview on /creator (that page is FTT/host/thrift only).
  const overview =
    vertical === 'moves'
      ? drivesHere
        ? DRIVING_OVERVIEW
        : MOVES_CUSTOMER_OVERVIEW
      : drivesHere && selling.length === 0
        ? DRIVING_OVERVIEW
        : drivesHere && !sellsHere
          ? DRIVING_OVERVIEW
          : WORK_OVERVIEW

  return [overview, ...selling, ...driving, ...WORK_TAIL]
}

export function getNavForRole(
  role: string | null,
  vertical: Vertical = DEFAULT_VERTICAL,
  /**
   * Per-vertical approval to sell, `{vertical: status}` from the profile (Spec 45).
   *
   * This replaced a `heldRoles.includes('creator')` check, which asked the wrong
   * question: the global `creator` role says someone sells *somewhere*, and showing a
   * thrift seller no prompt on Stays left them with no route to hosting at all. The
   * prompt now follows the vertical in front of them. `pending` and `rejected` still
   * link through — one to the review notice, the other to a form they can resubmit.
   */
  enrolments: Record<string, string> = {},
  /** Marketplace roles the account holds — used for Moves fulfiller affordances. */
  heldRoles: string[] = [],
): NavItem[] {
  const isFulfiller = heldRoles.includes('fulfiller')
  switch (role) {
    case 'consumer':
      return [
        ...CONSUMER_HEAD,
        ...(CONSUMER_BY_VERTICAL[vertical] ?? CONSUMER_BY_VERTICAL[DEFAULT_VERTICAL]),
        // Moves supply is fulfiller, not seller — only when not already a fulfiller.
        // Market verticals: hide "Become a …" once active or pending (drafting) here.
        ...(vertical === 'moves'
          ? isFulfiller
            ? []
            : // Not Truck — Book a move already uses it in this nav; PackageCheck is claim work.
              [{ label: 'Become a Fulfiller', path: '/become-fulfiller', icon: PackageCheck }]
          : shouldOfferBecomeSeller(vertical, enrolments)
            ? [becomeSellerItem(vertical)]
            : []),
        ...CONSUMER_SHARED,
      ]
    case 'creator':
      return [
        ...CREATOR_HEAD,
        ...(CREATOR_BY_VERTICAL[vertical] ?? CREATOR_BY_VERTICAL[DEFAULT_VERTICAL]),
        ...CREATOR_SHARED,
      ]
    case 'fulfiller':
      return FULFILLER_NAV
    case 'ops':
      return OPS_NAV
    case 'admin':
      return ADMIN_NAV
    default:
      return ONBOARDING_NAV
  }
}

export type { NavItem }

/**
 * The five entries the mobile tab bar shows.
 *
 * Falls back to the first five for navs that flag nothing (ops, admin, onboarding),
 * so this stays additive rather than something every nav has to opt into.
 */
export function mobileNavFor(items: NavItem[]): NavItem[] {
  const flagged = items.filter((i) => i.primary)
  return (flagged.length ? flagged : items).slice(0, 5)
}
